import { UploadOlkypassLvl2Response } from "@/features/user/document/types";
import { fetchServer } from "@/features/utils/fetch";
import { StandardResponse } from "@/features/utils/types";
import { DocumentAction } from "@/molecules/consent-document/ConsentDocument.react";
import { LinkSchemas } from "@/types/legal-entity/links.type";
import { ApiError } from "@/utils/internal-code-handler";
import {
  authenticatedCompanyFormType,
  CheckJoinLegalEntityResponse,
  CompanyPaymentInformations,
  CompanyQueryListResponse,
  companyDetails,
  DeclarativeCompany,
  emailInvitSchema,
  GetLinkInfoRequest,
  InitCardTransactionResponse,
  JoinLegalEntityRequest,
  LegalEntityCreateSchema,
  LegalEntityLinkDto,
  ProVerifiedUploadDocumentBody,
  rcsGetPayloadRequest
} from "../types";

export class CompanyApiClient {
  static CompanyRelatedPersonAddOrEditDto() {
    throw new Error("Method not implemented.");
  }
  static addCompanyInfo = async (data: LegalEntityCreateSchema) => {
    const response = await fetchServer("/company", {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };
  static createConsentDocument = async (companyUuid: string) => {
    const response = await fetchServer(`/company/${companyUuid}/consentdocument`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };

  static signConsentDocument = async ({
    status,
    comment,
    idCompany
  }: {
    status: DocumentAction;
    comment: string | null;
    idCompany?: string | null;
  }) => {
    if (!idCompany) throw new Error("Error");
    const response = await fetchServer(`/company/${idCompany}/consent-document/sign`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: {
        status,
        comment
      }
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };

  static getConsentDocument = async (companyUuid: string) => {
    const response = await fetchServer(`/company/${companyUuid}/consent-document`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new Error("Error");
    return response.json() as Promise<{
      success: boolean;
      data: string | null;
    }>;
  };

  static async checkJoinLegalEntityRequest(
    realUuid: string
  ): Promise<StandardResponse<CheckJoinLegalEntityResponse>> {
    const response = await fetchServer(`/company/${realUuid}/check-join`, {
      method: "GET"
    });
    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return await response.json();
  }

  static joinLegalEntityRequest = async (
    data: JoinLegalEntityRequest
  ): Promise<StandardResponse<CheckJoinLegalEntityResponse>> => {
    const response = await fetchServer(`/company/${data.legalEntityUuid}/join`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return response.json();
  };
  static getLinkInfo = async (
    data: GetLinkInfoRequest
  ): Promise<StandardResponse<LegalEntityLinkDto>> => {
    const response = await fetchServer(`/company/${data.leUuid}/links/${data.linkUuid}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return response.json();
  };
  static addCompanyDetectedRcsInfo = async (data: rcsGetPayloadRequest) => {
    const { country, rcs } = data;
    const response = await fetchServer(`/company/search/${country}/${rcs}`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };
  static async fetchOlkypass(realUuid: string): Promise<StandardResponse<boolean>> {
    try {
      const response = await fetchServer(`/company/searcholkypass/${realUuid}`, {
        method: "GET"
      });
      if (!response.ok) throw new Error("Error");

      return await response.json();
    } catch (_) {
      return { success: false, data: false };
    }
  }

  static completeCompanyAdd = async (uuidLe: string) => {
    const response = await fetchServer(`/company/${uuidLe}/complete`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: {}
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };
  static sendEmailCompany = async (data: emailInvitSchema) => {
    const response = await fetchServer(
      `company/${data.companyUuid}/links/${data.linkOlkyPassProUuid}/invite`,
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: {
          lang: data.lang,
          email: data.email
        }
      }
    );

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }
    return response.json();
  };
  static editCompanyForm = async (data: companyDetails, uuidLegalEntity: string) => {
    const response = await fetchServer("/company/" + uuidLegalEntity, {
      method: "PUT",
      reloadOn401: false,
      mainRoot: false,
      body: data.legalEntity
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };

  static partialEditCompany = async (data: DeclarativeCompany) => {
    const { legalEntityUuid, ...payload } = data;
    const response = await fetchServer("/company/" + legalEntityUuid, {
      method: "PATCH",
      reloadOn401: false,
      mainRoot: false,
      body: payload
    });
    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return response.json();
  };

  static addCompanyRepresentativeInfo = async (
    data: LegalEntityLinkDto,
    companyUuid: string
  ): Promise<
    StandardResponse<{
      linkUuid: string;
      legalEntityUuid: string;
    }>
  > => {
    const response = await fetchServer(`/company/${companyUuid}/links`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });

    if (!response.ok) throw new ApiError(await response.json(), response.status);
    return response.json();
  };
  static editCompanyRepresentativeInfo = async (
    data: LegalEntityLinkDto,
    companyUuid: string,
    uuidLink: string
  ): Promise<StandardResponse<string>> => {
    const response = await fetchServer(`/company/${companyUuid}/links/${uuidLink}`, {
      method: "PUT",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });

    if (!response.ok) throw new Error("Error");
    return response.json();
  };

  static async fetchListOfCompanies(): Promise<CompanyQueryListResponse> {
    try {
      const response = await fetchServer("/company", {
        method: "GET"
      });
      if (!response.ok) throw new Error("Error");

      return await response.json();
    } catch (_) {
      return { success: false, data: [] };
    }
  }

  static async getAllLinks(legalEntityUuid: string): Promise<LinkSchemas[]> {
    const response = await fetchServer(`/company/${legalEntityUuid}/links`, {
      method: "GET"
    });

    const res = await response.json();

    if (!response.ok) {
      throw new ApiError(res, response.status);
    }

    return res.data;
  }

  static async fetchCompanyDetails(uuidCompany: string): Promise<companyDetails | null> {
    try {
      const response = await fetchServer("/company/" + uuidCompany);
      return await response.json();
    } catch (_) {
      return null;
    }
  }
  static async fetchCompanyDetailsByAdmin(uuidCompany: string): Promise<companyDetails | null> {
    try {
      const response = await fetchServer("/company/admin/" + uuidCompany);
      return await response.json();
    } catch (_) {
      return null;
    }
  }
  static deleteLink = async (
    companyUuid: string,
    linkUuid: string
  ): Promise<StandardResponse<null>> => {
    const response = await fetchServer(`/company/${companyUuid}/links/${linkUuid}`, {
      method: "DELETE"
    });
    if (!response.ok) throw new Error("Error");
    return response.json();
  };

  static async uploadDocumentIdentity(
    data: ProVerifiedUploadDocumentBody
  ): Promise<StandardResponse<UploadOlkypassLvl2Response>> {
    if (!data.document && !data.identityDocumentType) {
      throw new Error("No document or type provided");
    }

    let base64WithFormat: string | undefined = undefined;
    if (data.document?.base64docs && data.document.fileFormat) {
      base64WithFormat = `data:${data.document.fileFormat};base64,${data.document.base64docs}`;
    }

    const response = await fetchServer(`/company/${data.companyUuid}/completeOlkyPassLvl2`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: {
        name: data.document?.name,
        base64docs: base64WithFormat,
        documentTypeCode: data.identityDocumentType?.code
      }
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }
    return await response.json();
  }

  static async editLevel3Form(
    data: authenticatedCompanyFormType,
    id: string
  ): Promise<StandardResponse<null>> {
    const response = await fetchServer(`/company/${id}/completeOlkyPassLvl3`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: data
    });
    if (!response.ok) throw new Error("Error");
    return await response.json();
  }

  static async initCardTransaction(
    legalEntityUuid: string,
    lang: string
  ): Promise<InitCardTransactionResponse> {
    const response = await fetchServer(`/company/${legalEntityUuid}/payments/card/session`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: { lang }
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }
    return (await response.json()).data;
  }

  static async fetchCompanyPaymentInformations(
    legalEntityUuid: string
  ): Promise<CompanyPaymentInformations> {
    const response = await fetchServer(`/company/${legalEntityUuid}/payments/iban`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    return (await response.json()).data;
  }

  static async initCompanyPayment(legalEntityUuid: string): Promise<CompanyPaymentInformations> {
    const response = await fetchServer(`/company/${legalEntityUuid}/payments/iban`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) {
      throw new ApiError(await response.json(), response.status);
    }

    return (await response.json()).data;
  }
}
